import { isPollStale } from "./freshness.js";

/**
 * What to show for each power supply, as StatusBadge statuses.
 *
 * Mirrors the psu_failed alert's rules so the page and the alert can never
 * tell two different stories: the supplies judged are the ones chosen when
 * set, otherwise every one that reports; a stale reading is shown as stale
 * rather than as the last state it happened to be in.
 */

export type PsuStatus = "ok" | "error" | "stale" | "never";

export interface PsuReading {
  psu_monitored: readonly (1 | 2)[] | null;
  psu1_state: number | null;
  psu2_state: number | null;
  psu_at: string | null;
  poll_interval_seconds: number;
}

export function psuDisplay(d: PsuReading, now: number): { label: string; status: PsuStatus }[] {
  // No reading ever: a board without PSU sensors. Showing nothing is the
  // honest answer; "unknown" badges on every RB2011 would be noise.
  if (!d.psu_at) return [];

  const states = [d.psu1_state, d.psu2_state];
  const indices =
    d.psu_monitored !== null
      ? [...new Set(d.psu_monitored)].sort().map((n) => n - 1)
      : states.flatMap((s, i) => (s === null ? [] : [i]));

  const stale = isPollStale(d.psu_at, d.poll_interval_seconds, now);
  return indices.map((i) => {
    const state = states[i];
    const status: PsuStatus = stale
      ? "stale"
      : state === 0
        ? "ok"
        : state === 1
          ? "error"
          : "never";
    return { label: `PSU ${i + 1}`, status };
  });
}
